/* eslint-disable max-lines */
import {css} from '@emotion/css';
import {colours, coloursV2, fonts, spacing} from '@fo/shared/styles/variables';
import {Breakpoints, mq} from '@fo/shared/styles/mixins';
import {ButtonVariant} from './types';

export default {
  container: (variant: ButtonVariant, isDisabled = false): string => css`
    label: button;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 3.9rem;
    padding: ${spacing.XXS} ${spacing.S};
    color: ${colours.general.WHITE};
    font-weight: ${fonts.weight.SEMI_BOLD};
    font-size: 1.6rem;
    font-family: ${fonts.family.PRIMARY};
    line-height: 1;
    text-align: center;
    text-decoration: none;
    background-color: ${colours.semantic.INFORMATION};
    border: 2px solid ${colours.semantic.INFORMATION};
    border-radius: 30px;
    outline: none;
    cursor: pointer;

    ${mq(Breakpoints.TABLET)} {
      width: auto;
    }

    &[disabled],
    &.disabled {
      pointer-events: none;
    }
    ${isDisabled && 'pointer-events: none;'};

    &:active {
      transform: scale(0.97);
    }

    ${variant === ButtonVariant.PRIMARY &&
    css`
      &:hover,
      &:focus {
        background-color: ${coloursV2.background.tideBlue};
        border-color: ${coloursV2.background.tideBlue};
      }

      &[disabled],
      &.disabled {
        background-color: ${colours.brand.SECONDARY_DISABLED};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      }
      ${isDisabled &&
      css`
        background-color: ${colours.brand.SECONDARY_DISABLED};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.TIDE_CTA &&
    css`
      &:hover,
      &:focus {
        background-color: ${coloursV2.background.tideBlue};
        border-color: ${coloursV2.background.tideBlue};
      }

      &[disabled],
      &.disabled {
        color: ${coloursV2.background.grey};
        background-color: ${colours.font.PLACEHOLDER_GRAY};
        border-color: ${colours.font.PLACEHOLDER_GRAY};
      }
      ${isDisabled &&
      css`
        background-color: ${colours.brand.SECONDARY_DISABLED};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.TIDE_SECONDARY_CTA &&
    css`
      color: ${coloursV2.font.tideBlue};
      background-color: ${colours.general.WHITE};
      border-color: ${colours.general.WHITE};

      &:hover,
      &:focus {
        color: ${colours.general.WHITE};
        background-color: ${colours.brand.SECONDARY_DISABLED};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      }

      &[disabled],
      &.disabled {
        color: ${coloursV2.background.grey};
        background-color: ${colours.font.PLACEHOLDER_GRAY};
        border-color: ${colours.font.PLACEHOLDER_GRAY};
      }
      ${isDisabled &&
      css`
        background-color: ${colours.brand.SECONDARY_DISABLED};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.PRIMARY_OUTLINE &&
    css`
      color: ${colours.font.BLACK};
      background-color: ${colours.general.WHITE};
      border-color: ${colours.semantic.INFORMATION};

      &:hover,
      &:focus {
        background-color: ${colours.brand.TERTIARY_DISABLED};
      }

      &[disabled],
      &.disabled {
        color: ${colours.semantic.PLACEHOLDER_ACTIVE};
        background-color: ${colours.general.WHITE};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      }
      ${isDisabled &&
      css`
        color: ${colours.semantic.PLACEHOLDER_ACTIVE};
        background-color: ${colours.general.WHITE};
        border-color: ${colours.brand.SECONDARY_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.CALL_TO_ACTION &&
    css`
      color: ${colours.font.BLACK};
      background-color: ${colours.brand.PRIMARY};
      border-color: ${colours.brand.PRIMARY};

      &:hover,
      &:focus {
        background-color: ${colours.semantic.WARNING_HOVER};
        border-color: ${colours.semantic.WARNING_HOVER};
      }

      &[disabled],
      &.disabled {
        color: ${colours.font.PLACEHOLDER_GRAY};
        background-color: ${colours.brand.PRIMARY_DISABLED};
        border-color: ${colours.brand.PRIMARY_DISABLED};
      }
      ${isDisabled &&
      css`
        color: ${colours.font.PLACEHOLDER_GRAY};
        background-color: ${colours.brand.PRIMARY_DISABLED};
        border-color: ${colours.brand.PRIMARY_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.CALL_TO_ACTION_OUTLINE &&
    css`
      color: ${colours.font.BLACK};
      background-color: ${colours.general.WHITE};
      border-color: ${colours.brand.PRIMARY};
      ${isDisabled &&
      css`
        color: ${colours.font.PLACEHOLDER_GRAY};
        border-color: ${colours.brand.PRIMARY_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.DEFAULT &&
    css`
      color: ${colours.font.BLACK};
      background-color: ${colours.border.LIGHT_GRAY};
      border-color: ${colours.border.LIGHT_GRAY};

      &:hover,
      &:focus {
        background-color: ${colours.semantic.DEFAULT_HOVER};
        border-color: ${colours.semantic.DEFAULT_HOVER};
      }

      &[disabled],
      &.disabled {
        color: ${colours.semantic.DEFAULT_HOVER};
        background-color: ${colours.field.DISABLED};
        border-color: ${colours.field.DISABLED};
      }
      ${isDisabled &&
      css`
        color: ${colours.semantic.DEFAULT_HOVER};
        background-color: ${colours.field.DISABLED};
        border-color: ${colours.field.DISABLED};
      `}
    `}

    ${variant === ButtonVariant.DEFAULT_OUTLINE &&
    css`
      color: ${colours.font.BLACK};
      background-color: ${colours.general.WHITE};
      border-color: ${colours.border.LIGHT_GRAY};
    `}

    ${variant === ButtonVariant.SUCCESS &&
    css`
      color: ${colours.font.BLACK};
      background-color: transparent;
      border-color: ${colours.semantic.SUCCESS};

      &:hover,
      &:active {
        background-color: ${colours.brand.TERTIARY};
      }

      &[disabled],
      &.disabled {
        color: ${colours.font.PLACEHOLDER_GRAY};
        border-color: ${colours.semantic.SUCCESS_DISABLED};
      }
      ${isDisabled &&
      css`
        color: ${colours.font.PLACEHOLDER_GRAY};
        border-color: ${colours.semantic.SUCCESS_DISABLED};
      `}
    `}

    ${variant === ButtonVariant.DEFAULT_TEXT &&
    css`
      // allows for vertical centering when passed in text is wrapped in <span style='display: block;'/>
      display: inline-flex;
      align-items: center;
      width: auto;
      height: auto;
      min-height: auto;
      padding: 0;
      color: ${colours.font.ASSISTIVE};
      text-align: left;
      background: none;
      border: none;
      border-radius: 0;

      &:hover {
        color: $semanticColorInformationHover;
        color: ${colours.semantic.INFORMATION_HOVER};
        svg {
          fill: currentColor;
        }
      }

      &[disabled],
      &.disabled {
        color: ${colours.font.LIGHT_GRAY};
      }
      ${isDisabled &&
      css`
        color: ${colours.font.LIGHT_GRAY};
      `};

      svg {
        margin: -0.4rem; // stylelint-disable-line sh-waqar/declaration-use-variable
        margin-right: 0;
      }

      span {
        display: block;
      }
    `}

    ${variant === ButtonVariant.LINK_TEXT &&
    css`
      // allows for vertical centering when passed in text is wrapped in <span style='display: block;'/>
      display: inline-flex;
      align-items: center;
      width: auto;
      height: auto;
      min-height: auto;
      padding: 0;
      color: ${colours.font.INFORMATION};
      font-weight: ${fonts.weight.REGULAR};
      text-align: left;
      text-decoration: underline;
      background: none;
      border: none;
      border-radius: 0;

      &:hover {
        color: ${colours.semantic.INFORMATION_HOVER};

        svg {
          fill: currentColor;
        }
      }

      &[disabled],
      &.disabled {
        color: ${colours.font.LIGHT_GRAY};
      }
      ${isDisabled && `color: ${colours.font.LIGHT_GRAY};`};

      svg {
        margin: -0.4rem; // stylelint-disable-line sh-waqar/declaration-use-variable
        margin-right: 0;
      }

      span {
        display: block;
      }
    `}

    ${variant === ButtonVariant.NEGATIVE &&
    css`
      color: ${colours.semantic.ERROR};
      background: none;
      border-color: transparent;
    `}

    ${variant === ButtonVariant.UNSTYLED &&
    css`
      display: inline-block;
      margin: 0;
      font-size: inherit;
      font-family: sans-serif;
      font-weight: inherit;
      text-align: center;
      text-decoration: none;
      color: inherit;
      background-color: transparent;
      border: none;
      cursor: pointer;
      transition: background 250ms ease-in-out, transform 150ms ease;
      appearance: none;
      padding: revert;
      min-height: auto;
    `}
  `,
  icon: css`
    label: button__icon;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: ${spacing.XXS};
  `,
};
